import { useMemo } from 'react';
import { UsersRound } from 'lucide-react';
import type { AbsenceCalendarRow } from '@ip/shared-types';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { ABSENCE_TYPE_META, absenceTypeLabel, formatDateRange, initials } from '@/lib/hr';
import { t } from '@/i18n';

interface WhosOutProps {
  /** Team calendar rows (the caller is filtered out by `selfEmployeeId`). */
  rows: AbsenceCalendarRow[];
  selfEmployeeId: string | null;
  /** Window the entries were fetched for (used to clamp displayed ranges). */
  from: string;
  to: string;
}

/**
 * "Who's out — next 7 days": names + absence type ONLY. Reasons and sick
 * detail are already stripped server-side for non-HR callers; this panel
 * additionally never renders them even if present (peer visibility rule).
 */
export function WhosOut({ rows, selfEmployeeId, from, to }: WhosOutProps) {
  const items = useMemo(() => {
    const out: Array<{
      key: string; name: string; first: string; last: string;
      type: AbsenceCalendarRow['entries'][number]['type'];
      from: string; to: string; pending: boolean;
    }> = [];
    for (const row of rows) {
      if (row.employeeId === selfEmployeeId) continue;
      for (const entry of row.entries) {
        if (entry.dateTo < from || entry.dateFrom > to) continue;
        out.push({
          key: entry.id,
          name: `${row.firstName} ${row.lastName}`,
          first: row.firstName,
          last: row.lastName,
          type: entry.type,
          from: entry.dateFrom,
          to: entry.dateTo,
          pending: entry.status === 'pending',
        });
      }
    }
    return out.sort((a, b) => (a.from < b.from ? -1 : 1));
  }, [rows, selfEmployeeId, from, to]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('hr.whosOut.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <div className="text-muted-foreground flex flex-col items-center gap-2 py-6 text-center text-sm">
            <UsersRound className="size-8 opacity-40" />
            <p className="max-w-56">{t('hr.whosOut.empty')}</p>
          </div>
        ) : (
          <ul className="space-y-3">
            {items.map((item) => {
              const meta = ABSENCE_TYPE_META[item.type];
              return (
                <li key={item.key} className="flex items-center gap-3">
                  <Avatar className="size-8">
                    <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
                      {initials(item.first, item.last)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{item.name}</p>
                    <p className="text-muted-foreground text-xs">
                      {formatDateRange(item.from, item.to)}
                      {item.pending && <> {t('hr.whosOut.pendingSuffix')}</>}
                    </p>
                  </div>
                  <span
                    className={cn(
                      'shrink-0 rounded-full border px-2 py-0.5 text-xs font-medium',
                      meta.chip,
                      item.pending && 'border-dashed',
                    )}
                  >
                    {absenceTypeLabel(item.type)}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
